<template>
  <!-- <AppToastCenter /> temporarily disabled for debugging freeze -->

  <div v-if="!isHydrated" class="app-hydrating">
    <div class="app-hydrating-card">
      <h1 class="app-hydrating-title">Loading session...</h1>
      <p class="app-hydrating-text">
        English: Restoring authentication state before choosing the application layout.
        ไทย: กำลังกู้คืนสถานะการยืนยันตัวตนก่อนเลือก layout ของแอป
      </p>
    </div>
  </div>

  <div v-else-if="currentLayout === 'blank'" class="app-blank">
    <router-view />
  </div>

  <div v-else-if="shouldRenderDashboardShell" class="app-shell">
    <header class="app-topbar">
      <div class="brand-block">
        <h1 class="app-title">Multi-Mission Fleet Dashboard</h1>
        <p class="app-subtitle">
          Realtime fleet operations, monitoring, and control.
        </p>
      </div>

      <nav v-if="isAuthenticated" class="topbar-nav" aria-label="Main navigation">
        <router-link class="topbar-nav-link" to="/dashboard">Dashboard</router-link>
        <router-link class="topbar-nav-link" to="/dashboard/live">Live</router-link>
        <router-link class="topbar-nav-link" to="/robots">Robots</router-link>
        <router-link class="topbar-nav-link" to="/missions">Missions</router-link>
        <router-link class="topbar-nav-link" to="/dashboard/map-builder">Map Builder</router-link>
        <router-link class="topbar-nav-link" to="/dashboard/command">Command</router-link>
        <router-link class="topbar-nav-link" to="/dashboard/schedules">Schedules</router-link>
        <router-link class="topbar-nav-link" to="/alerts">Alerts</router-link>
        <router-link class="topbar-nav-link" to="/company/staff">Staff</router-link>
        <router-link class="topbar-nav-link" to="/company/sites">Sites</router-link>
        <router-link
          v-if="isSuperAdmin"
          class="topbar-nav-link topbar-nav-link--admin"
          to="/admin/dashboard"
        >Admin</router-link>
      </nav>

      <div class="topbar-right">
        <UserMenu />
      </div>
    </header>

    <main class="app-main">
      <aside class="app-sidebar">
        <nav v-if="isAuthenticated" class="sidebar-nav" aria-label="Sidebar navigation">
          <div class="sidebar-nav-section">
            <p class="sidebar-nav-section-label">Fleet</p>
            <router-link class="sidebar-nav-item" to="/dashboard">
              <span class="sidebar-nav-icon" aria-hidden="true">📊</span>Dashboard
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/live">
              <span class="sidebar-nav-icon" aria-hidden="true">📡</span>Live Fleet
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/channels">
              <span class="sidebar-nav-icon" aria-hidden="true">📶</span>Channel Explorer
            </router-link>
            <router-link class="sidebar-nav-item" to="/missions">
              <span class="sidebar-nav-icon" aria-hidden="true">🗺️</span>Missions
            </router-link>
            <router-link class="sidebar-nav-item" to="/missions/planner">
              <span class="sidebar-nav-icon" aria-hidden="true">📋</span>Mission Planner
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/map-builder">
              <span class="sidebar-nav-icon" aria-hidden="true">📐</span>Map Builder
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/command">
              <span class="sidebar-nav-icon" aria-hidden="true">🎮</span>Command
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/schedules">
              <span class="sidebar-nav-icon" aria-hidden="true">📅</span>Schedules
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/analytics">
              <span class="sidebar-nav-icon" aria-hidden="true">📈</span>Fleet Analytics
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/maintenance">
              <span class="sidebar-nav-icon" aria-hidden="true">🔧</span>Maintenance Log
            </router-link>
            <router-link class="sidebar-nav-item" to="/alerts">
              <span class="sidebar-nav-icon" aria-hidden="true">🔔</span>Alerts
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/notifications">
              <span class="sidebar-nav-icon" aria-hidden="true">📬</span>Notifications
            </router-link>
          </div>

          <div class="sidebar-nav-section">
            <p class="sidebar-nav-section-label">Testing & Monitoring</p>
            <router-link class="sidebar-nav-item" to="/dashboard/motor-test">
              <span class="sidebar-nav-icon" aria-hidden="true">⚙️</span>Motor Test
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/lidar-test">
              <span class="sidebar-nav-icon" aria-hidden="true">📡</span>LiDAR Test
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/system-metrics">
              <span class="sidebar-nav-icon" aria-hidden="true">📊</span>System Metrics
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/historical">
              <span class="sidebar-nav-icon" aria-hidden="true">📈</span>Historical Data
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/behavior-log">
              <span class="sidebar-nav-icon" aria-hidden="true">🧠</span>Behavior Log
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/slam-map">
              <span class="sidebar-nav-icon" aria-hidden="true">🗺️</span>SLAM Map
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/persons">
              <span class="sidebar-nav-icon" aria-hidden="true">👤</span>Known Persons
            </router-link>
          </div>

          <div class="sidebar-nav-section">
            <p class="sidebar-nav-section-label">Robots</p>
            <router-link class="sidebar-nav-item" to="/robots">
              <span class="sidebar-nav-icon" aria-hidden="true">🤖</span>Overview
            </router-link>
            <router-link class="sidebar-nav-item" to="/robots/add">
              <span class="sidebar-nav-icon" aria-hidden="true">➕</span>Add Robot
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/thresholds">
              <span class="sidebar-nav-icon" aria-hidden="true">⚡</span>Thresholds
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/connectivity-policy">
              <span class="sidebar-nav-icon" aria-hidden="true">🔌</span>Connectivity Policy
            </router-link>
            <router-link class="sidebar-nav-item" to="/robots/api-keys">
              <span class="sidebar-nav-icon" aria-hidden="true">🔑</span>API Keys
            </router-link>
            <router-link class="sidebar-nav-item" to="/robots/hardware-monitor">
              <span class="sidebar-nav-icon" aria-hidden="true">🔍</span>Hardware Monitor
            </router-link>
            <router-link class="sidebar-nav-item" to="/robots/transit-config">
              <span class="sidebar-nav-icon" aria-hidden="true">📍</span>Transit Config
            </router-link>
          </div>

          <div class="sidebar-nav-section">
            <p class="sidebar-nav-section-label">Calibration</p>
            <router-link class="sidebar-nav-item" to="/dashboard/calibration">
              <span class="sidebar-nav-icon" aria-hidden="true">🧭</span>Steering
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/calibration/blade">
              <span class="sidebar-nav-icon" aria-hidden="true">🌀</span>Blade
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/calibration/nav">
              <span class="sidebar-nav-icon" aria-hidden="true">🛤️</span>Navigation
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/calibration/scrub">
              <span class="sidebar-nav-icon" aria-hidden="true">💧</span>Scrubbing
            </router-link>
          </div>

          <div class="sidebar-nav-section">
            <p class="sidebar-nav-section-label">Video</p>
            <router-link class="sidebar-nav-item" to="/dashboard/video">
              <span class="sidebar-nav-icon" aria-hidden="true">📹</span>Video Viewer
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/vision-policy">
              <span class="sidebar-nav-icon" aria-hidden="true">👁️</span>Vision Policy
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/gcs-config">
              <span class="sidebar-nav-icon" aria-hidden="true">☁️</span>GCS Config
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/video-retention">
              <span class="sidebar-nav-icon" aria-hidden="true">🗂️</span>Retention Policy
            </router-link>
          </div>

          <div class="sidebar-nav-section">
            <p class="sidebar-nav-section-label">Settings</p>
            <router-link class="sidebar-nav-item" to="/company/staff">
              <span class="sidebar-nav-icon" aria-hidden="true">👥</span>Staff
            </router-link>
            <router-link class="sidebar-nav-item" to="/company/sites">
              <span class="sidebar-nav-icon" aria-hidden="true">🏢</span>Sites
            </router-link>
            <router-link class="sidebar-nav-item" to="/dashboard/webhooks">
              <span class="sidebar-nav-icon" aria-hidden="true">🔗</span>Webhooks
            </router-link>
          </div>

          <div v-if="isSuperAdmin" class="sidebar-nav-section">
            <p class="sidebar-nav-section-label">Admin</p>
            <router-link class="sidebar-nav-item sidebar-nav-item--admin" to="/admin/dashboard">
              <span class="sidebar-nav-icon" aria-hidden="true">⚙️</span>Admin Dashboard
            </router-link>
          </div>
        </nav>
      </aside>

      <section class="app-content">
        <router-view />
      </section>
    </main>
  </div>

  <div v-else class="app-session-redirecting">
    <div class="app-session-redirecting-card">
      <h1 class="app-session-redirecting-title">Redirecting...</h1>
      <p class="app-session-redirecting-text">
        English: Current route requires an authenticated session. Redirecting to a safe page.
        ไทย: route ปัจจุบันต้องใช้ session ที่ยืนยันตัวตนแล้ว ระบบกำลังกำหนดเส้นทางไปยังหน้าที่ปลอดภัย
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, watch, onUnmounted, onErrorCaptured } from 'vue';
import { useRoute } from 'vue-router';
import UserMenu from './components/auth/UserMenu.vue';
// import AppToastCenter from './components/common/AppToastCenter.vue'; // disabled for isolation test
import { useAuth } from './composables/useAuth/useAuth.js';

const APP_TRACE_NAMESPACE = '[App.vue][RUNTIME_TRACE]';
const APP_TRACE_ENABLED = false;

function appTrace(step, payload = null) {
  if (!APP_TRACE_ENABLED) return;
  // English: Use console.log — these are informational trace events, not errors.
  //          Using console.error here would flood DevTools' error channel with
  //          non-error events if tracing is re-enabled.
  // ไทย: ใช้ console.log — events เหล่านี้คือ trace ข้อมูล ไม่ใช่ error
  //      ถ้าใช้ console.error จะทำให้ DevTools error channel เต็มด้วย non-error
  //      เมื่อเปิด tracing ขึ้นมาอีกครั้ง
  try {
    console.log(`${APP_TRACE_NAMESPACE} ${step}`, {
      ts: new Date().toISOString(),
      step,
      payload,
    });
  } catch (_error) {
    console.log(`${APP_TRACE_NAMESPACE} ${step}`);
  }
}

function summarizeMeta(meta) {
  return {
    layout: meta?.layout || null,
    section: meta?.section || null,
    navGroup: meta?.navGroup || null,
    requiresAuth: Boolean(meta?.requiresAuth),
    permissionsCount: Array.isArray(meta?.permissions) ? meta.permissions.length : 0,
  };
}

// Debug gate — Layer 1/2/3:
// English: OFF by default. Enable by appending ?debug=1 to the URL or setting
//          localStorage.setItem('dashboard_debug', '1') in DevTools.
// ไทย: ปิดโดยค่าเริ่มต้น เปิดได้โดยเพิ่ม ?debug=1 ใน URL หรือตั้ง
//      localStorage.setItem('dashboard_debug', '1') ใน DevTools

const debugEnabled = false;
const route = useRoute();

appTrace('SETUP_START', {
  href: window.location.href,
  initialRoutePath: route?.fullPath || null,
  initialRouteName: route?.name || null,
});

// Auth state — drives role-conditional navigation
const { isAuthenticated, user, isHydrated, getSessionSnapshot } = useAuth();

appTrace('AUTH_STATE_BOUND', {
  hasIsAuthenticatedRef: Boolean(isAuthenticated),
  hasUserRef: Boolean(user),
  hasIsHydratedRef: Boolean(isHydrated),
});



// English: isSuperAdmin gates /admin/dashboard link — page file exists only for this role.
// Thai: isSuperAdmin ควบคุม link /admin/dashboard — page file มีเฉพาะ role นี้
const isSuperAdmin = computed(() => user.value?.role === 'super_admin');

const currentRouteName = computed(() => route?.name || 'UNKNOWN_ROUTE');
const currentPath = computed(() => route?.path || '/');
const currentFullPath = computed(() => route?.fullPath || '/');
const currentLayout = computed(() => route?.meta?.layout || 'NO_LAYOUT');
const currentSection = computed(() => route?.meta?.section || 'NO_SECTION');

const currentRequiresAuth = computed(() => Boolean(route?.meta?.requiresAuth));

// English: Temporary runtime isolation switch for Site CRUD.
//          This prevents SiteListPage / SiteSetupPage from mounting while the post-login
//          freeze is being investigated. Keep this local to App.vue so the rest of the
//          dashboard remains usable.
// ไทย: สวิตช์แยกปัญหา runtime ชั่วคราวสำหรับ Site CRUD
//      ป้องกันไม่ให้ SiteListPage / SiteSetupPage ถูก mount ระหว่างสืบสวนปัญหาค้างหลังล็อกอิน
//      จำกัดผลกระทบไว้ที่ App.vue เพื่อให้ส่วนอื่นของ dashboard ยังใช้งานได้
/* const isSiteCrudTemporarilyDisabled = true;
const isSiteCrudRoute = computed(() => {
  const path = route?.path || '';
  return path === '/company/sites' || path.startsWith('/company/sites/');
}); */


const shouldRenderDashboardShell = computed(() => {
  if (!isHydrated.value) return false;
  if (currentLayout.value === 'blank') return false;
  if (!currentRequiresAuth.value) return true;
  return Boolean(isAuthenticated.value);
});

const shouldRedirectProtectedGuest = computed(() => {
  if (!isHydrated.value) return false;
  if (currentLayout.value === 'blank') return false;
  if (!currentRequiresAuth.value) return false;
  return !isAuthenticated.value;
});

const appDebugSnapshot = computed(() => ({
  route: {
    name: currentRouteName.value,
    path: currentPath.value,
    fullPath: currentFullPath.value,
    layout: currentLayout.value,
    section: currentSection.value,
    requiresAuth: currentRequiresAuth.value,
  },
  auth: {
    isAuthenticated: Boolean(isAuthenticated?.value),
    isHydrated: Boolean(isHydrated?.value),
    shouldRenderDashboardShell: shouldRenderDashboardShell.value,
    shouldRedirectProtectedGuest: shouldRedirectProtectedGuest.value,
    sessionSnapshot: typeof getSessionSnapshot === 'function' ? getSessionSnapshot() : null,
  },
}));

const routeTraceSnapshot = computed(() => ({
  routeName: currentRouteName.value,
  path: currentPath.value,
  fullPath: currentFullPath.value,
  layout: currentLayout.value,
  section: currentSection.value,
  requiresAuth: currentRequiresAuth.value,
  isAuthenticated: Boolean(isAuthenticated?.value),
  isHydrated: Boolean(isHydrated?.value),
  shouldRenderDashboardShell: shouldRenderDashboardShell.value,
  shouldRedirectProtectedGuest: shouldRedirectProtectedGuest.value,
  userRole: user?.value?.role || null,
  isSuperAdmin: isSuperAdmin.value,
}));



// English: Guard argument evaluation — routeTraceSnapshot.value reads 11+ reactive sources.
//          Accessing it as a bare argument pre-evaluates it even when APP_TRACE_ENABLED = false.
// Thai: guard การ evaluate argument — routeTraceSnapshot.value อ่าน reactive source 11+ ตัว
//       การส่งเป็น argument ตรงๆ จะ evaluate มันก่อนเรียกฟังก์ชัน แม้ APP_TRACE_ENABLED = false
if (APP_TRACE_ENABLED) {
  appTrace('SETUP_DONE', routeTraceSnapshot.value);
}

function stringifyRaw(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch (_error) {
    return 'Unable to stringify app debug snapshot / ไม่สามารถแปลง app debug snapshot เป็นข้อความได้';
  }
}

let routeChangeCount = 0;
let snapshotChangeCount = 0;
let authStateChangeCount = 0;
let lifecycleHeartbeatId = null;

// English: All three trace watchers are guarded by APP_TRACE_ENABLED so they are never
//          registered when tracing is off. Previously each watcher was registered
//          unconditionally: the callback had an early return but Vue still tracked all
//          reactive deps and fired the watcher on every route/auth change regardless.
//          Wrapping in if (APP_TRACE_ENABLED) eliminates the dep tracking entirely.
// ไทย: watchers ทั้งสามถูก guard ด้วย APP_TRACE_ENABLED เพื่อไม่ให้ลงทะเบียนเมื่อ tracing ปิด
//      เดิมลงทะเบียนเสมอ — callback มี early return แต่ Vue ยัง track reactive deps ทั้งหมด
//      และ fire watcher ทุกครั้งที่ route/auth เปลี่ยน การ wrap ด้วย if ตัดการ track ออกทั้งหมด
if (APP_TRACE_ENABLED) {
  watch(
    () => route.fullPath,
    (nextValue, previousValue) => {
      routeChangeCount += 1;
      appTrace('ROUTE_FULLPATH_CHANGED', {
        count: routeChangeCount,
        previousFullPath: previousValue || null,
        nextFullPath: nextValue || null,
        routeName: currentRouteName.value,
        meta: summarizeMeta(route.meta || {}),
      });
    },
    { immediate: false },
  );

  // English: Watch individual scalar computed refs instead of routeTraceSnapshot (which always
  //          returns a new object reference on every evaluation). Vue compares scalar values by
  //          value, so this watcher fires ONLY when a field actually changes — not on every
  //          reactive update that touches any of the 11+ snapshot dependencies.
  //          routeTraceSnapshot itself is kept for use inside APP_TRACE_ENABLED guarded branches.
  // Thai: watch scalar computed refs แต่ละตัวแทน routeTraceSnapshot (ซึ่งคืน object reference
  //       ใหม่ทุกครั้งที่ evaluate) Vue เปรียบเทียบ scalar ด้วยค่า ดังนั้น watcher นี้ fire
  //       เฉพาะเมื่อ field เปลี่ยนจริง — ไม่ใช่ทุก reactive update ที่แตะ dependency ใน snapshot
  //       routeTraceSnapshot เก็บไว้สำหรับใช้ใน branch ที่ถูก guard ด้วย APP_TRACE_ENABLED
  watch(
    [
      currentFullPath,
      currentLayout,
      () => Boolean(isAuthenticated?.value),
      () => Boolean(isHydrated?.value),
      () => user?.value?.role || null,
    ],
    (
      [nextPath, nextLayout, nextAuth, nextHydrated, nextRole],
      [prevPath, prevLayout, prevAuth, prevHydrated, prevRole],
    ) => {
      snapshotChangeCount += 1;
      appTrace('ROUTE_TRACE_SNAPSHOT_CHANGED', {
        count: snapshotChangeCount,
        prev: { path: prevPath, layout: prevLayout, auth: prevAuth, hydrated: prevHydrated, role: prevRole },
        next: { path: nextPath, layout: nextLayout, auth: nextAuth, hydrated: nextHydrated, role: nextRole },
      });
    },
    { immediate: false },
  );

  // English: Replace the inline object factory () => ({...}) with individual scalar getters.
  //          An object factory always produces a new reference — Vue's deep:false comparison
  //          sees prev !== next on every call and schedules the callback unconditionally.
  //          Individual scalar getters are compared by value: callback fires only on real changes.
  // Thai: แทน inline object factory () => ({...}) ด้วย scalar getters แต่ละตัว
  //       object factory คืน reference ใหม่เสมอ — การเปรียบเทียบ deep:false ของ Vue
  //       เห็น prev !== next ทุกครั้งและ schedule callback โดยไม่มีเงื่อนไข
  //       scalar getter แต่ละตัวเปรียบเทียบด้วยค่า: callback fire เฉพาะเมื่อเปลี่ยนจริง
  watch(
    [
      () => Boolean(isAuthenticated?.value),
      () => Boolean(isHydrated?.value),
      () => user?.value?.role || null,
      () => user?.value?.user_id || user?.value?.id || null,
    ],
    (
      [nextIsAuth, nextIsHydrated, nextRole, nextId],
      [prevIsAuth, prevIsHydrated, prevRole, prevId],
    ) => {
      authStateChangeCount += 1;
      appTrace('AUTH_STATE_CHANGED', {
        count: authStateChangeCount,
        prev: { isAuthenticated: prevIsAuth, isHydrated: prevIsHydrated, role: prevRole, userId: prevId },
        next: { isAuthenticated: nextIsAuth, isHydrated: nextIsHydrated, role: nextRole, userId: nextId },
      });
    },
    { immediate: false },
  );
}


onUnmounted(() => {
  if (lifecycleHeartbeatId !== null) {
    window.clearInterval(lifecycleHeartbeatId);
    lifecycleHeartbeatId = null;
  }

  if (!APP_TRACE_ENABLED) return;

  appTrace('ON_UNMOUNTED', {
    routeChangeCount,
    snapshotChangeCount,
    authStateChangeCount,
  });
});

// English: Dedup guard — prevents DevTools freeze when a child component throws the same
//          error repeatedly (e.g. reactive re-render loop). Within _ERROR_DEDUP_WINDOW_MS,
//          identical (message + componentName) pairs are silently suppressed after the first log.
// ไทย: Dedup guard — ป้องกัน DevTools freeze เมื่อ child component throw error ซ้ำๆ
//      (เช่น reactive re-render loop) ภายใน _ERROR_DEDUP_WINDOW_MS คู่ (message + componentName)
//      เดิมจะถูก suppress โดยไม่ log หลังจาก log ครั้งแรกแล้ว
const _errorDedupMap = new Map();
const _ERROR_DEDUP_WINDOW_MS = 500;

// English: onErrorCaptured fires for every Vue component error in the tree.
//          We always log the error message and component name so production incidents
//          are visible in logs/monitoring. Stack trace and Vue diagnostic info are
//          only included when debugEnabled=true — they add no value in production and
//          can be large enough to cause console spam.
// ไทย: onErrorCaptured ยิงทุกครั้งที่มี component error ใน tree
//      เราล็อก error message และ component name เสมอเพื่อให้ production incidents มองเห็นได้
//      stack trace และ Vue diagnostic info แสดงเฉพาะเมื่อ debugEnabled=true —
//      ใน production ไม่มีประโยชน์และอาจทำให้ console spam
onErrorCaptured((error, instance, info) => {
  const now = Date.now();
  const msg = error?.message || String(error);
  const comp = instance?.type?.name || instance?.type?.__name || 'unknown';
  const dedupKey = `${msg}||${comp}`;

  const lastSeen = _errorDedupMap.get(dedupKey);
  if (lastSeen !== undefined && (now - lastSeen) < _ERROR_DEDUP_WINDOW_MS) {
    // Suppress repeated identical error — do not log again within dedup window
    return false;
  }
  _errorDedupMap.set(dedupKey, now);
  // Prune stale entries to prevent memory growth on long-running sessions
  if (_errorDedupMap.size > 100) {
    for (const [k, t] of _errorDedupMap) {
      if ((now - t) > _ERROR_DEDUP_WINDOW_MS * 4) _errorDedupMap.delete(k);
    }
  }

  console.error('[App.vue][ERROR_CAPTURED]', {
    message_en: 'Vue component error captured at App root.',
    message_th: 'Vue component error ถูก capture ที่ App root',
    errorMessage: msg,
    componentName: comp,
    // Stack and Vue info only in debug mode — verbose in production, spammy for transient errors
    ...(debugEnabled ? {
      stack: error?.stack || null,
      info: info || null,
    } : {}),
  });

  return false;
});

</script>

<style scoped>
.app-hydrating {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f3f7fb;
}

.app-hydrating-card {
  width: min(100%, 520px);
  padding: 24px;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
  background: #ffffff;
  text-align: center;
}

.app-hydrating-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #102a43;
}

.app-hydrating-text {
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.6;
  color: #52606d;
}

.app-session-redirecting {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f3f7fb;
}

.app-session-redirecting-card {
  width: min(100%, 520px);
  padding: 24px;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
  background: #ffffff;
  text-align: center;
}

.app-session-redirecting-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #102a43;
}

.app-session-redirecting-text {
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.6;
  color: #52606d;
}

/* Blank layout — no chrome, just the page */
.app-blank {
  min-height: 100vh;
}
.brand-block {
  min-width: 0;
}


/* ── Topbar navigation ─────────────────────────────────────────────────── */
.topbar-nav {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  padding: 0 16px;
}

.topbar-nav-link {
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #334e68;
  text-decoration: none;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}

.topbar-nav-link:hover,
.topbar-nav-link.router-link-active {
  background: #e8f0fe;
  color: #1a56db;
}

.topbar-nav-link--admin {
  color: #9333ea;
}

.topbar-nav-link--admin:hover,
.topbar-nav-link--admin.router-link-active {
  background: #f3e8ff;
  color: #7e22ce;
}

/* ── Sidebar navigation ────────────────────────────────────────────────── */
.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  background: #ffffff;
  border: 1px solid #d9e2ec;
  border-radius: 12px;
}

.sidebar-nav-section {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 8px;
  margin-bottom: 4px;
  border-bottom: 1px solid #eef2f7;
}

.sidebar-nav-section:last-child {
  border-bottom: none;
  padding-bottom: 0;
  margin-bottom: 0;
}

.sidebar-nav-section-label {
  margin: 0 0 4px 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #7b8794;
}

.sidebar-nav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #334e68;
  text-decoration: none;
  transition: background 0.12s, color 0.12s;
}

.sidebar-nav-item:hover,
.sidebar-nav-item.router-link-active {
  background: #e8f0fe;
  color: #1a56db;
  font-weight: 600;
}

.sidebar-nav-item--admin {
  color: #9333ea;
}

.sidebar-nav-item--admin:hover,
.sidebar-nav-item--admin.router-link-active {
  background: #f3e8ff;
  color: #7e22ce;
}

.sidebar-nav-icon {
  font-size: 15px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.app-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background: #f3f7fb;
  color: #102a43;
}

.app-topbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #d9e2ec;
  border-radius: 14px;
}

.app-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
}

.app-subtitle {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.6;
  color: #52606d;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}



.app-main {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

.app-sidebar {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.app-content {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

@media (max-width: 960px) {
  .app-main {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .app-topbar {
    flex-direction: column;
  }
}
</style>